import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { invoiceService } from '../services/invoiceService'
import { generateInvoicePDF } from '../utils/pdfGenerator'
import type { Invoice } from '../types/invoice'
import { 
  CameraIcon,
  ArrowLeftIcon,
  PencilIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  PrinterIcon,
  ShareIcon,
  UserIcon,
  EnvelopeIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import ProfileDropdown from '../components/ProfileDropdown'

const InvoiceViewPage = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    if (user && id) {
      loadInvoice()
    }
  }, [user, id])

  const loadInvoice = async () => {
    if (!id) return
    
    setIsLoading(true)
    const result = await invoiceService.getInvoiceById(id)
    
    if (result.success && result.invoice) {
      setInvoice(result.invoice)
    } else {
      console.error('Failed to load invoice:', result.error)
      navigate('/invoices')
    }
    setIsLoading(false)
  }

  const handleStatusUpdate = async (status: Invoice['status']) => {
    if (!invoice) return
    
    setIsUpdating(true)
    const result = await invoiceService.updateInvoice(invoice.id, { status })
    
    if (result.success && result.invoice) {
      setInvoice(result.invoice)
      showSuccess(
        'Status Updated',
        `Invoice ${invoice.invoiceNumber} status has been updated to ${status}.`
      )
    } else {
      showError(
        'Update Failed',
        result.error || 'Failed to update invoice status. Please try again.'
      )
    }
    setIsUpdating(false)
  }

  const handleMarkAsPaid = async () => {
    if (!invoice) return
    
    setIsUpdating(true)
    const result = await invoiceService.updateInvoice(invoice.id, {
      status: 'paid',
      paymentStatus: 'paid',
      paymentDate: new Date().toISOString().split('T')[0]
    })
    
    if (result.success && result.invoice) {
      setInvoice(result.invoice)
      showSuccess(
        'Payment Recorded',
        `Invoice ${invoice.invoiceNumber} has been marked as paid. Your monthly income has been updated.`
      )
      
      // Trigger a custom event to refresh dashboard data
      const eventDetail = { invoiceId: invoice.id, newStatus: 'paid', amount: invoice.totalAmount }
      console.log('Dispatching invoiceStatusChanged event:', eventDetail)
      window.dispatchEvent(new CustomEvent('invoiceStatusChanged', {
        detail: eventDetail
      }))
    } else {
      showError(
        'Payment Failed',
        result.error || 'Failed to mark invoice as paid. Please try again.'
      )
    }
    setIsUpdating(false)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'draft':
        return 'text-gray-400 bg-gray-500/20'
      case 'sent':
        return 'text-blue-400 bg-blue-500/20'
      case 'viewed':
        return 'text-purple-400 bg-purple-500/20'
      case 'paid':
        return 'text-green-400 bg-green-500/20'
      case 'overdue':
        return 'text-red-400 bg-red-500/20'
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/20'
      default:
        return 'text-gray-400 bg-gray-500/20'
    }
  }

  const isOverdue = () => {
    if (!invoice || invoice.status === 'paid') return false
    return new Date(invoice.dueDate) < new Date()
  }

  const handleDownloadPDF = async () => {
    if (!invoice) return
    
    try {
      const pdfBlob = generateInvoicePDF(invoice, user)
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoice.invoiceNumber}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      showSuccess(
        'PDF Generated',
        `Invoice ${invoice.invoiceNumber} has been downloaded as PDF.`
      )
    } catch (error) {
      console.error('Error generating PDF:', error)
      showError(
        'PDF Generation Failed',
        `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`
      )
    }
  }

  const handlePrintInvoice = () => {
    if (!invoice) return
    
    try {
      const pdfBlob = generateInvoicePDF(invoice, user)
      const url = URL.createObjectURL(pdfBlob)
      
      // Open PDF in new window for printing
      const printWindow = window.open(url, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
      
      // Clean up URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      
      showSuccess(
        'Print Ready',
        'Invoice has been opened in a new window for printing.'
      )
    } catch (error) {
      console.error('Error printing invoice:', error)
      showError(
        'Print Failed',
        'Failed to prepare invoice for printing. Please try again.'
      )
    }
  }

  if (!user) {
    return <div>Please log in to view invoices.</div>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading invoice...</p>
        </div>
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8 text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Invoice Not Found</h2>
          <p className="text-gray-400 mb-6">The invoice you're looking for doesn't exist or you don't have permission to view it.</p>
          <Link to="/invoices">
            <button className="btn-primary">Back to Invoices</button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <motion.button 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-6"
            >
              <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors font-medium">
                Dashboard
              </Link>
              <ProfileDropdown />
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Link 
            to="/invoices"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Invoices</span>
          </Link>
        </motion.div>

        {/* Invoice Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-mockup rounded-xl p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <h1 className="text-3xl font-bold text-white">{invoice.invoiceNumber}</h1>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
                  <span className="capitalize">{invoice.status}</span>
                </div>
                {isOverdue() && invoice.status !== 'paid' && (
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-red-400 bg-red-500/20">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    Overdue
                  </div>
                )}
              </div>
              <h2 className="text-xl text-gray-300 mb-1">{invoice.title}</h2>
              <p className="text-gray-400">
                Created {formatDate(invoice.createdAt)} • Due {formatDate(invoice.dueDate)}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {invoice.status !== 'paid' && (
                <button
                  onClick={handleMarkAsPaid}
                  disabled={isUpdating}
                  className="btn-success flex items-center justify-center space-x-2 disabled:opacity-50 w-full sm:w-auto"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>Mark as Paid</span>
                </button>
              )}
              
              {invoice.status === 'draft' && (
                <button
                  onClick={() => handleStatusUpdate('sent')}
                  disabled={isUpdating}
                  className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 w-full sm:w-auto"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                  <span>Send Invoice</span>
                </button>
              )}
              
              <Link to={`/invoices/${invoice.id}/edit`} className="w-full sm:w-auto">
                <button className="btn-secondary flex items-center justify-center space-x-2 w-full">
                  <PencilIcon className="w-5 h-5" />
                  <span>Edit</span>
                </button>
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Invoice Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Client Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <UserIcon className="w-6 h-6" />
                <span>Bill To</span>
              </h3>
              
              {invoice.client ? (
                <div className="space-y-3">
                  <div>
                    <h4 className="text-lg font-medium text-white">{invoice.client.name}</h4>
                    {invoice.client.company && (
                      <p className="text-gray-400">{invoice.client.company}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 text-gray-400">
                    <EnvelopeIcon className="w-4 h-4" />
                    <span>{invoice.client.email}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">Client information not available</p>
              )}
            </motion.div>

            {/* Line Items */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <DocumentTextIcon className="w-6 h-6" />
                <span>Items</span>
              </h3>
              
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 text-sm font-medium text-gray-400">Description</th>
                      <th className="text-right py-3 text-sm font-medium text-gray-400">Qty</th>
                      <th className="text-right py-3 text-sm font-medium text-gray-400">Rate</th>
                      <th className="text-right py-3 text-sm font-medium text-gray-400">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {invoice.items?.map((item) => (
                      <tr key={item.id}>
                        <td className="py-4">
                          <div>
                            <div className="font-medium text-white">{item.description}</div>
                            {item.notes && (
                              <div className="text-sm text-gray-400 mt-1">{item.notes}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 text-right text-white">{item.quantity}</td>
                        <td className="py-4 text-right text-white">{formatCurrency(item.unitPrice)}</td>
                        <td className="py-4 text-right font-medium text-white">{formatCurrency(item.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {invoice.items?.map((item) => (
                  <div key={item.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 pr-4">
                        <h4 className="font-medium text-white mb-1">{item.description}</h4>
                        {item.notes && (
                          <p className="text-sm text-gray-400">{item.notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">{formatCurrency(item.totalPrice)}</div>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <div className="text-gray-400">
                        Qty: <span className="text-white">{item.quantity}</span>
                      </div>
                      <div className="text-gray-400">
                        Rate: <span className="text-white">{formatCurrency(item.unitPrice)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Additional Information */}
            {(invoice.description || invoice.notes || invoice.terms) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-mockup rounded-xl p-6"
              >
                <h3 className="text-xl font-semibold text-white mb-4">Additional Information</h3>
                
                <div className="space-y-4">
                  {invoice.description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                      <p className="text-white">{invoice.description}</p>
                    </div>
                  )}
                  
                  {invoice.terms && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Terms & Conditions</h4>
                      <p className="text-white whitespace-pre-wrap">{invoice.terms}</p>
                    </div>
                  )}
                  
                  {invoice.notes && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Notes</h4>
                      <p className="text-white whitespace-pre-wrap">{invoice.notes}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Invoice Summary */}
          <div className="space-y-8">
            {/* Financial Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <CurrencyDollarIcon className="w-6 h-6" />
                <span>Summary</span>
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                
                {invoice.discountAmount > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>Discount:</span>
                    <span>-{formatCurrency(invoice.discountAmount)}</span>
                  </div>
                )}
                
                {invoice.taxRate > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>Tax ({invoice.taxRate}%):</span>
                    <span>{formatCurrency(invoice.taxAmount)}</span>
                  </div>
                )}
                
                <div className="border-t border-white/10 pt-3">
                  <div className="flex justify-between text-white font-semibold text-lg">
                    <span>Total:</span>
                    <span>{formatCurrency(invoice.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Payment Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">Payment Status</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className={`font-medium capitalize ${
                    invoice.paymentStatus === 'paid' ? 'text-green-400' : 
                    invoice.paymentStatus === 'partial' ? 'text-yellow-400' : 
                    'text-gray-400'
                  }`}>
                    {invoice.paymentStatus}
                  </span>
                </div>
                
                {invoice.paymentDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Paid Date:</span>
                    <span className="text-white">{formatDate(invoice.paymentDate)}</span>
                  </div>
                )}
                
                {invoice.paymentMethod && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Method:</span>
                    <span className="text-white">{invoice.paymentMethod}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-400">Due Date:</span>
                  <span className={`font-medium ${
                    isOverdue() && invoice.status !== 'paid' ? 'text-red-400' : 'text-white'
                  }`}>
                    {formatDate(invoice.dueDate)}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="glass-mockup rounded-xl p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button 
                  onClick={handleDownloadPDF}
                  className="w-full btn-primary flex items-center justify-center space-x-2"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Download PDF</span>
                </button>
                
                <button 
                  onClick={handlePrintInvoice}
                  className="w-full btn-secondary flex items-center justify-center space-x-2"
                >
                  <PrinterIcon className="w-5 h-5" />
                  <span>Print Invoice</span>
                </button>
                
                <button className="w-full btn-secondary flex items-center justify-center space-x-2">
                  <ShareIcon className="w-5 h-5" />
                  <span>Share Invoice</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default InvoiceViewPage 